import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    ArrowLeft, Link2, DollarSign, BarChart3, Users, Copy, Plus,
    Loader2, RefreshCw, LogOut, TrendingUp, MousePointerClick,
    UserPlus, CreditCard, Clock, CheckCircle, XCircle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { affiliateApi } from '../lib/api';

const AffiliateDashboard = ({ onBack }) => {
    const [sessionId, setSessionId] = useState(localStorage.getItem('eskrybe_affiliate_session'));
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [referrals, setReferrals] = useState([]);
    const [payouts, setPayouts] = useState(null);
    const [customLinks, setCustomLinks] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [analyticsPeriod, setAnalyticsPeriod] = useState('30d');

    // Login/Signup form state
    const [authMode, setAuthMode] = useState('login');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        website: '',
        marketing_channels: ''
    });
    const [authLoading, setAuthLoading] = useState(false);

    // New link form
    const [newLinkData, setNewLinkData] = useState({ campaign: '', source: '' });
    const [creatingLink, setCreatingLink] = useState(false);

    // Payout request form
    const [payoutData, setPayoutData] = useState({ amount: '', payout_method: 'paypal', payout_details: '' });
    const [requestingPayout, setRequestingPayout] = useState(false);

    useEffect(() => {
        if (sessionId) {
            verifySession();
        } else {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (isAuthenticated && sessionId) {
            loadAnalytics();
        }
    }, [analyticsPeriod, isAuthenticated, sessionId]);

    const verifySession = async () => {
        try {
            const response = await affiliateApi.getDashboard(sessionId);
            setDashboardData(response.data);
            setIsAuthenticated(true);
            loadData();
        } catch (error) {
            localStorage.removeItem('eskrybe_affiliate_session');
            setSessionId(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    const loadData = async () => {
        try {
            const [linksRes, referralsRes, payoutsRes] = await Promise.all([
                affiliateApi.getLinks(sessionId),
                affiliateApi.getReferrals(sessionId),
                affiliateApi.getPayouts(sessionId)
            ]);
            setCustomLinks(linksRes.data.custom_links || []);
            setReferrals(referralsRes.data.referrals || []);
            setPayouts(payoutsRes.data);
            loadAnalytics();
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    const loadAnalytics = async () => {
        try {
            const response = await affiliateApi.getAnalytics(sessionId, analyticsPeriod);
            setAnalytics(response.data);
        } catch (error) {
            console.error('Failed to load analytics:', error);
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        try {
            if (authMode === 'login') {
                const response = await affiliateApi.login({
                    email: formData.email,
                    password: formData.password
                });
                const newSessionId = response.data.session_id;
                localStorage.setItem('eskrybe_affiliate_session', newSessionId);
                setSessionId(newSessionId);
                
                // Directly fetch dashboard with the new session ID instead of relying on state
                const dashboardResponse = await affiliateApi.getDashboard(newSessionId);
                setDashboardData(dashboardResponse.data);
                setIsAuthenticated(true);
                
                // Load additional data
                const [linksRes, referralsRes, payoutsRes] = await Promise.all([
                    affiliateApi.getLinks(newSessionId),
                    affiliateApi.getReferrals(newSessionId),
                    affiliateApi.getPayouts(newSessionId)
                ]);
                setCustomLinks(linksRes.data.custom_links || []);
                setReferrals(referralsRes.data.referrals || []);
                setPayouts(payoutsRes.data);
                
                // Load analytics
                const analyticsResponse = await affiliateApi.getAnalytics(newSessionId, analyticsPeriod);
                setAnalytics(analyticsResponse.data);
                
                toast.success('Welcome back!');
            } else {
                const response = await affiliateApi.signup({
                    name: formData.name,
                    email: formData.email,
                    password: formData.password,
                    website: formData.website,
                    marketing_channels: formData.marketing_channels
                });
                toast.success('Application submitted! You will be notified once approved.');
                setAuthMode('login');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Authentication failed');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await affiliateApi.logout(sessionId);
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('eskrybe_affiliate_session');
        setSessionId(null);
        setIsAuthenticated(false);
        setDashboardData(null);
    };

    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link);
        toast.success('Link copied to clipboard!');
    };

    const handleCreateLink = async (e) => {
        e.preventDefault();
        if (!newLinkData.campaign) {
            toast.error('Campaign name is required');
            return;
        }
        setCreatingLink(true);
        try {
            await affiliateApi.createCustomLink(sessionId, newLinkData.campaign, newLinkData.source);
            toast.success('Custom link created!');
            setNewLinkData({ campaign: '', source: '' });
            loadData();
        } catch (error) {
            toast.error('Failed to create link');
        } finally {
            setCreatingLink(false);
        }
    };

    const handleRequestPayout = async (e) => {
        e.preventDefault();
        const amount = parseFloat(payoutData.amount);
        if (isNaN(amount) || amount <= 0) {
            toast.error('Invalid amount');
            return;
        }
        if (!payoutData.payout_details) {
            toast.error('Please provide payout details');
            return;
        }
        setRequestingPayout(true);
        try {
            await affiliateApi.requestPayout(sessionId, {
                amount,
                payout_method: payoutData.payout_method,
                payout_details: payoutData.payout_details
            });
            toast.success('Payout request submitted!');
            setPayoutData({ amount: '', payout_method: 'paypal', payout_details: '' });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to request payout');
        } finally {
            setRequestingPayout(false);
        }
    };

    const handleCancelPayout = async (requestId) => {
        try {
            await affiliateApi.cancelPayoutRequest(sessionId, requestId);
            toast.success('Payout request cancelled');
            loadData();
        } catch (error) {
            toast.error('Failed to cancel request');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Auth form
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background">
                <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                    <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                        <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </button>
                        <div className="flex items-center gap-2">
                            <Link2 className="h-5 w-5 text-accent" />
                            <span className="font-semibold">Affiliate Portal</span>
                        </div>
                        <div className="w-16" />
                    </div>
                </header>

                <div className="max-w-md mx-auto mt-12 px-6">
                    <Card>
                        <CardHeader className="text-center">
                            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                                <Link2 className="h-8 w-8 text-accent" />
                            </div>
                            <CardTitle>{authMode === 'login' ? 'Affiliate Login' : 'Become an Affiliate'}</CardTitle>
                            <CardDescription>
                                {authMode === 'login' 
                                    ? 'Access your affiliate dashboard'
                                    : 'Earn 30% on first payments, 15% on recurring'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleAuth} className="space-y-4">
                                {authMode === 'signup' && (
                                    <div>
                                        <Label htmlFor="name">Name</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                            data-testid="affiliate-name-input"
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        data-testid="affiliate-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        data-testid="affiliate-password-input"
                                    />
                                </div>
                                {authMode === 'signup' && (
                                    <>
                                        <div>
                                            <Label htmlFor="website">Website (optional)</Label>
                                            <Input
                                                id="website"
                                                value={formData.website}
                                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                                placeholder="https://yoursite.com"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="channels">How will you promote eSkrybe?</Label>
                                            <Input
                                                id="channels"
                                                value={formData.marketing_channels}
                                                onChange={(e) => setFormData({ ...formData, marketing_channels: e.target.value })}
                                                placeholder="Blog, YouTube, Newsletter..."
                                            />
                                        </div>
                                    </>
                                )}
                                <Button type="submit" className="w-full" disabled={authLoading} data-testid="affiliate-submit-btn">
                                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {authMode === 'login' ? 'Login' : 'Apply to Join'}
                                </Button>
                            </form>
                            <div className="mt-4 text-center">
                                <button
                                    className="text-sm text-accent hover:underline"
                                    onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
                                >
                                    {authMode === 'login' ? "Don't have an account? Apply now" : 'Already an affiliate? Login'}
                                </button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Commission Info */}
                    <div className="mt-8 grid grid-cols-2 gap-4">
                        <Card className="p-4 text-center">
                            <p className="text-3xl font-bold text-accent">30%</p>
                            <p className="text-sm text-muted-foreground">First Payment</p>
                        </Card>
                        <Card className="p-4 text-center">
                            <p className="text-3xl font-bold text-accent">15%</p>
                            <p className="text-sm text-muted-foreground">Recurring</p>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    // Affiliate Dashboard
    const affiliate = dashboardData?.affiliate;
    const stats = dashboardData?.stats;

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" />
                        Back to App
                    </button>
                    <div className="flex items-center gap-2">
                        <Link2 className="h-5 w-5 text-accent" />
                        <span className="font-semibold">Affiliate Dashboard</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={loadData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleLogout}>
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Welcome & Referral Link */}
                <Card className="mb-8">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-semibold">Welcome, {affiliate?.name}!</h2>
                                <p className="text-muted-foreground">Your affiliate code: <span className="text-accent font-mono">{affiliate?.code}</span></p>
                            </div>
                            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                                <Input
                                    value={affiliate?.referral_link || ''}
                                    readOnly
                                    className="bg-transparent border-none w-72"
                                />
                                <Button size="sm" variant="outline" onClick={() => handleCopyLink(affiliate?.referral_link)}>
                                    <Copy className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/10 rounded-lg">
                                    <MousePointerClick className="h-5 w-5 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.total_clicks || 0}</p>
                                    <p className="text-xs text-muted-foreground">Total Clicks</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-500/10 rounded-lg">
                                    <UserPlus className="h-5 w-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
                                    <p className="text-xs text-muted-foreground">Referrals</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-500/10 rounded-lg">
                                    <TrendingUp className="h-5 w-5 text-purple-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.conversion_rate || 0}%</p>
                                    <p className="text-xs text-muted-foreground">Conversion</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-500/10 rounded-lg">
                                    <DollarSign className="h-5 w-5 text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">${stats?.total_earnings?.toFixed(2) || '0.00'}</p>
                                    <p className="text-xs text-muted-foreground">Total Earnings</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="links">Links</TabsTrigger>
                        <TabsTrigger value="analytics">Analytics</TabsTrigger>
                        <TabsTrigger value="payouts">Payouts</TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Earnings Summary */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Earnings Summary</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                                        <span>Available for Withdrawal</span>
                                        <span className="text-xl font-bold text-accent">${stats?.pending_payout?.toFixed(2) || '0.00'}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                                        <span>Already Paid Out</span>
                                        <span className="font-medium">${stats?.paid_out?.toFixed(2) || '0.00'}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                                        <span>This Month</span>
                                        <span className="font-medium text-green-500">+${stats?.monthly_earnings?.toFixed(2) || '0.00'}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Minimum payout: ${stats?.min_payout_threshold || 50}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Recent Referrals */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Recent Referrals</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {dashboardData?.recent_referrals?.length === 0 ? (
                                        <p className="text-center text-muted-foreground py-8">No referrals yet</p>
                                    ) : (
                                        <div className="space-y-3">
                                            {dashboardData?.recent_referrals?.slice(0, 5).map(ref => (
                                                <div key={ref.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                                    <div>
                                                        <p className="text-sm font-medium">{ref.user_email}</p>
                                                        <p className="text-xs text-muted-foreground">{new Date(ref.created_at).toLocaleDateString()}</p>
                                                    </div>
                                                    <Badge variant={ref.status === 'converted' ? 'default' : 'secondary'}>
                                                        {ref.status === 'converted' ? `+$${ref.commission_earned?.toFixed(2)}` : 'Pending'}
                                                    </Badge>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Commission Rates */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Your Commission Rates</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-accent/10 rounded-lg text-center">
                                            <p className="text-3xl font-bold text-accent">{(affiliate?.commission_rate_first * 100)}%</p>
                                            <p className="text-sm text-muted-foreground">First Payment</p>
                                        </div>
                                        <div className="p-4 bg-accent/10 rounded-lg text-center">
                                            <p className="text-3xl font-bold text-accent">{(affiliate?.commission_rate_recurring * 100)}%</p>
                                            <p className="text-sm text-muted-foreground">Recurring</p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-4 text-center">
                                        Cookie duration: {affiliate?.cookie_days} days
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Links Tab */}
                    <TabsContent value="links">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Create New Link */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Create Campaign Link</CardTitle>
                                    <CardDescription>Track performance by campaign or source</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleCreateLink} className="space-y-4">
                                        <div>
                                            <Label htmlFor="campaign">Campaign Name *</Label>
                                            <Input
                                                id="campaign"
                                                value={newLinkData.campaign}
                                                onChange={(e) => setNewLinkData({ ...newLinkData, campaign: e.target.value })}
                                                placeholder="e.g., summer_promo"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="source">Source (optional)</Label>
                                            <Input
                                                id="source"
                                                value={newLinkData.source}
                                                onChange={(e) => setNewLinkData({ ...newLinkData, source: e.target.value })}
                                                placeholder="e.g., twitter, youtube"
                                            />
                                        </div>
                                        <Button type="submit" disabled={creatingLink}>
                                            {creatingLink ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                                            Create Link
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>

                            {/* Custom Links List */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Your Links</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ScrollArea className="h-[300px]">
                                        <div className="space-y-3">
                                            {/* Default Link */}
                                            <div className="p-3 border rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Badge>Default</Badge>
                                                    <Button size="sm" variant="ghost" onClick={() => handleCopyLink(affiliate?.referral_link)}>
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-muted-foreground font-mono truncate">{affiliate?.referral_link}</p>
                                            </div>

                                            {/* Custom Links */}
                                            {customLinks.map(link => (
                                                <div key={link.id} className="p-3 border rounded-lg">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline">{link.campaign}</Badge>
                                                            {link.source && <Badge variant="secondary">{link.source}</Badge>}
                                                        </div>
                                                        <Button size="sm" variant="ghost" onClick={() => handleCopyLink(link.link)}>
                                                            <Copy className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground font-mono truncate">{link.link}</p>
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                        <span>{link.clicks || 0} clicks</span>
                                                        <span>{link.conversions || 0} conversions</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Analytics Tab */}
                    <TabsContent value="analytics">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Performance Analytics</CardTitle>
                                    <div className="flex gap-2">
                                        {['7d', '30d', '90d', '365d'].map(period => (
                                            <Button
                                                key={period}
                                                variant={analyticsPeriod === period ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setAnalyticsPeriod(period)}
                                            >
                                                {period}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {analytics && (
                                    <>
                                        {/* Summary Stats */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                            <div className="p-4 bg-muted/50 rounded-lg text-center">
                                                <p className="text-2xl font-bold">{analytics.summary?.total_clicks || 0}</p>
                                                <p className="text-xs text-muted-foreground">Clicks</p>
                                            </div>
                                            <div className="p-4 bg-muted/50 rounded-lg text-center">
                                                <p className="text-2xl font-bold">{analytics.summary?.total_signups || 0}</p>
                                                <p className="text-xs text-muted-foreground">Signups</p>
                                            </div>
                                            <div className="p-4 bg-muted/50 rounded-lg text-center">
                                                <p className="text-2xl font-bold">{analytics.summary?.total_conversions || 0}</p>
                                                <p className="text-xs text-muted-foreground">Conversions</p>
                                            </div>
                                            <div className="p-4 bg-muted/50 rounded-lg text-center">
                                                <p className="text-2xl font-bold">${analytics.summary?.total_earnings?.toFixed(2) || '0.00'}</p>
                                                <p className="text-xs text-muted-foreground">Earnings</p>
                                            </div>
                                        </div>

                                        {/* Traffic Sources */}
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div>
                                                <h4 className="font-medium mb-3">Traffic Sources</h4>
                                                <div className="space-y-2">
                                                    {Object.entries(analytics.traffic_sources || {}).map(([source, count]) => (
                                                        <div key={source} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                                                            <span className="capitalize">{source || 'Direct'}</span>
                                                            <Badge variant="secondary">{count}</Badge>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-medium mb-3">Campaign Performance</h4>
                                                <div className="space-y-2">
                                                    {analytics.campaign_performance?.map(camp => (
                                                        <div key={camp.campaign} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                                                            <span>{camp.campaign || 'No Campaign'}</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm text-muted-foreground">{camp.clicks} clicks</span>
                                                                <Badge>{camp.conversions} conv</Badge>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Payouts Tab */}
                    <TabsContent value="payouts">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Request Payout */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Request Payout</CardTitle>
                                    <CardDescription>
                                        Available: ${payouts?.pending_balance?.toFixed(2) || '0.00'} | Minimum: ${payouts?.min_threshold || 50}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {payouts?.pending_requests?.length > 0 ? (
                                        <div className="space-y-3">
                                            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Clock className="h-4 w-4 text-yellow-500" />
                                                    <span className="font-medium">Pending Request</span>
                                                </div>
                                                {payouts.pending_requests.map(req => (
                                                    <div key={req.id} className="flex items-center justify-between">
                                                        <span className="text-lg font-bold">${req.amount.toFixed(2)}</span>
                                                        <Button size="sm" variant="outline" onClick={() => handleCancelPayout(req.id)}>
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleRequestPayout} className="space-y-4">
                                            <div>
                                                <Label htmlFor="amount">Amount ($)</Label>
                                                <Input
                                                    id="amount"
                                                    type="number"
                                                    step="0.01"
                                                    min={payouts?.min_threshold || 50}
                                                    max={payouts?.pending_balance || 0}
                                                    value={payoutData.amount}
                                                    onChange={(e) => setPayoutData({ ...payoutData, amount: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="method">Payout Method</Label>
                                                <select
                                                    id="method"
                                                    className="w-full p-2 border rounded-md bg-background"
                                                    value={payoutData.payout_method}
                                                    onChange={(e) => setPayoutData({ ...payoutData, payout_method: e.target.value })}
                                                >
                                                    <option value="paypal">PayPal</option>
                                                    <option value="bank_transfer">Bank Transfer</option>
                                                </select>
                                            </div>
                                            <div>
                                                <Label htmlFor="details">
                                                    {payoutData.payout_method === 'paypal' ? 'PayPal Email' : 'Bank Details'}
                                                </Label>
                                                <Input
                                                    id="details"
                                                    value={payoutData.payout_details}
                                                    onChange={(e) => setPayoutData({ ...payoutData, payout_details: e.target.value })}
                                                    placeholder={payoutData.payout_method === 'paypal' ? 'your@email.com' : 'Bank name, account number...'}
                                                    required
                                                />
                                            </div>
                                            <Button 
                                                type="submit" 
                                                disabled={requestingPayout || (payouts?.pending_balance || 0) < (payouts?.min_threshold || 50)}
                                                className="w-full"
                                            >
                                                {requestingPayout ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
                                                Request Payout
                                            </Button>
                                        </form>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Payout History */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Payout History</CardTitle>
                                    <CardDescription>Total paid: ${payouts?.total_paid?.toFixed(2) || '0.00'}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <ScrollArea className="h-[300px]">
                                        {payouts?.payout_history?.length === 0 ? (
                                            <p className="text-center text-muted-foreground py-8">No payouts yet</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {payouts?.payout_history?.map(payout => (
                                                    <div key={payout.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                        <div>
                                                            <p className="font-medium">${payout.amount.toFixed(2)}</p>
                                                            <p className="text-xs text-muted-foreground">{new Date(payout.created_at).toLocaleDateString()}</p>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="secondary">{payout.method}</Badge>
                                                            <Badge className="bg-green-500">
                                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                                Paid
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default AffiliateDashboard;
